import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Users,
  Link,
  Copy,
  Share2,
  Plus,
  Trash2,
  Edit,
  Eye,
  Calendar,
  GraduationCap,
  School,
  CheckCircle,
} from "lucide-react";

interface ClassTemplate {
  id: string;
  className: string;
  templateId: string;
  templateName: string;
  templatePreview: string;
  description: string;
  createdDate: string;
  totalStudents: number;
  completedDesigns: number;
  link: string;
}

interface ClassTemplateSelectorProps {
  onGenerateClassLink: (className: string, templateId: string) => void;
}

export const ClassTemplateSelector = ({
  onGenerateClassLink,
}: ClassTemplateSelectorProps) => {
  const [activeTab, setActiveTab] = useState("create");
  const [className, setClassName] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [classDescription, setClassDescription] = useState("");

  // Mock templates - in real app, this would come from API
  const availableTemplates = [
    {
      id: "sash-1",
      name: "Traditional Gold",
      preview: "/api/placeholder/120/80",
      description: "Classic gold and black traditional pattern",
    },
    {
      id: "sash-2",
      name: "Royal Blue",
      preview: "/api/placeholder/120/80",
      description: "Elegant blue and white royal pattern",
    },
    {
      id: "sash-3",
      name: "Graduation Special",
      preview: "/api/placeholder/120/80",
      description: "Special graduation ceremony design",
    },
    {
      id: "sash-4",
      name: "Heritage Pattern",
      preview: "/api/placeholder/120/80",
      description: "Traditional heritage design",
    },
    {
      id: "sash-5",
      name: "Modern Twist",
      preview: "/api/placeholder/120/80",
      description: "Contemporary design with traditional elements",
    },
    {
      id: "sash-6",
      name: "Classic Red",
      preview: "/api/placeholder/120/80",
      description: "Bold red and gold classic pattern",
    },
  ];

  // Mock existing class templates
  const existingClassTemplates: ClassTemplate[] = [
    {
      id: "class-1",
      className: "Computer Science Class of 2025",
      templateId: "sash-1",
      templateName: "Traditional Gold",
      templatePreview: "/api/placeholder/120/80",
      description: "Graduation ceremony for CS students",
      createdDate: "2025-01-10",
      totalStudents: 45,
      completedDesigns: 32,
      link: "https://kenteweave.com/designer?class=CS2025&template=sash-1",
    },
    {
      id: "class-2",
      className: "Business Administration 2025",
      templateId: "sash-3",
      templateName: "Graduation Special",
      templatePreview: "/api/placeholder/120/80",
      description: "Business school graduation",
      createdDate: "2025-01-08",
      totalStudents: 28,
      completedDesigns: 28,
      link: "https://kenteweave.com/designer?class=BA2025&template=sash-3",
    },
    {
      id: "class-3",
      className: "Engineering Class of 2025",
      templateId: "sash-2",
      templateName: "Royal Blue",
      templatePreview: "/api/placeholder/120/80",
      description: "Engineering graduation ceremony",
      createdDate: "2025-01-05",
      totalStudents: 67,
      completedDesigns: 45,
      link: "https://kenteweave.com/designer?class=ENG2025&template=sash-2",
    },
  ];

  const handleCreateClass = () => {
    if (className && selectedTemplate) {
      onGenerateClassLink(className, selectedTemplate);
      // Reset form
      setClassName("");
      setSelectedTemplate("");
      setClassDescription("");
      setActiveTab("manage");
    }
  };

  const copyClassLink = (link: string) => {
    navigator.clipboard.writeText(link);
    // You could add a toast notification here
  };

  const getCompletionPercentage = (completed: number, total: number) => {
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="create" className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Create Class Link
          </TabsTrigger>
          <TabsTrigger value="manage" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Manage Classes
          </TabsTrigger>
        </TabsList>

        {/* Create Class Link Tab */}
        <TabsContent value="create" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5" />
                Create Class-Specific Designer Link
              </CardTitle>
              <CardDescription>
                Select a template and create a link that restricts students to
                that specific template
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Class Information */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="class-name">Class Name</Label>
                  <Input
                    id="class-name"
                    placeholder="e.g., Computer Science Class of 2025"
                    value={className}
                    onChange={(e) => setClassName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="class-description">
                    Description (Optional)
                  </Label>
                  <Input
                    id="class-description"
                    placeholder="Brief description of the class or event"
                    value={classDescription}
                    onChange={(e) => setClassDescription(e.target.value)}
                  />
                </div>
              </div>

              {/* Template Selection */}
              <div className="space-y-4">
                <Label>Select Template for This Class</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {availableTemplates.map((template) => (
                    <Card
                      key={template.id}
                      className={`cursor-pointer transition-all hover:shadow-md ${
                        selectedTemplate === template.id
                          ? "ring-2 ring-primary border-primary"
                          : ""
                      }`}
                      onClick={() => setSelectedTemplate(template.id)}
                    >
                      <CardContent className="p-4">
                        <div className="aspect-video bg-muted rounded-lg mb-3 flex items-center justify-center">
                          <div className="text-center">
                            <School className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground">
                              {template.name}
                            </p>
                          </div>
                        </div>
                        <h3 className="font-semibold mb-1">{template.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {template.description}
                        </p>
                        {selectedTemplate === template.id && (
                          <div className="mt-2 flex items-center gap-1 text-primary">
                            <CheckCircle className="h-4 w-4" />
                            <span className="text-sm font-medium">
                              Selected
                            </span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Create Button */}
              <div className="flex justify-end">
                <Button
                  onClick={handleCreateClass}
                  disabled={!className || !selectedTemplate}
                  className="min-w-[200px]"
                >
                  <Link className="h-4 w-4 mr-2" />
                  Create Class Link
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Manage Classes Tab */}
        <TabsContent value="manage" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Your Class Links
              </CardTitle>
              <CardDescription>
                Manage and track your class-specific designer links
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {existingClassTemplates.map((classTemplate) => (
                  <Card
                    key={classTemplate.id}
                    className="hover:shadow-md transition-shadow"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          {/* Template Preview */}
                          <div className="w-20 h-14 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
                            <School className="h-6 w-6 text-muted-foreground" />
                          </div>

                          {/* Class Info */}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-lg">
                                {classTemplate.className}
                              </h3>
                              <Badge variant="secondary">
                                {classTemplate.templateName}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">
                              {classTemplate.description}
                            </p>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                Created {classTemplate.createdDate}
                              </div>
                              <div className="flex items-center gap-1">
                                <Users className="h-4 w-4" />
                                {classTemplate.totalStudents} students
                              </div>
                            </div>

                            {/* Progress Bar */}
                            <div className="mt-3">
                              <div className="flex justify-between text-sm mb-1">
                                <span>Designs Completed</span>
                                <span>
                                  {classTemplate.completedDesigns}/
                                  {classTemplate.totalStudents} (
                                  {getCompletionPercentage(
                                    classTemplate.completedDesigns,
                                    classTemplate.totalStudents
                                  )}
                                  %)
                                </span>
                              </div>
                              <div className="w-full bg-muted rounded-full h-2">
                                <div
                                  className="bg-primary h-2 rounded-full transition-all"
                                  style={{
                                    width: `${getCompletionPercentage(
                                      classTemplate.completedDesigns,
                                      classTemplate.totalStudents
                                    )}%`,
                                  }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 ml-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyClassLink(classTemplate.link)}
                          >
                            <Copy className="h-4 w-4 mr-2" />
                            Copy Link
                          </Button>
                          <Button variant="outline" size="sm">
                            <Share2 className="h-4 w-4 mr-2" />
                            Share
                          </Button>
                          <Button variant="outline" size="sm">
                            <Eye className="h-4 w-4 mr-2" />
                            View
                          </Button>
                          <Button variant="outline" size="sm">
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {existingClassTemplates.length === 0 && (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">
                    No class links created yet
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Create your first class-specific designer link to get
                    started
                  </p>
                  <Button onClick={() => setActiveTab("create")}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Class Link
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
